import { Link, Outlet, useLocation } from 'react-router-dom';
import { UserMenu } from '../UserMenu';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { AImryAssistant } from '@/features/support';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { usePlugins } from '@/contexts/PluginContext';
import { useAuthStore } from '@/features/auth';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { useTranslation } from 'react-i18next';
import { BookmarkCheck, CalendarDays, DoorOpen } from 'lucide-react';
import useMediaQuery, { MOBILE_BREAKPOINT } from '@/shared/hooks/useMediaQuery';

/**
 * TopNavLayout Component
 * Traditional top navigation bar layout
 */
export default function TopNavLayout() {
  const location = useLocation();
  const { t } = useTranslation();
  const { isPluginEnabled } = usePlugins();
  const { user: _user } = useAuthStore();
  const { settings } = useSystemSettings();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path);

  // Icon mapping for navigation items
  const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
    '/bookings/my-bookings': BookmarkCheck,
    '/calendar': CalendarDays,
    '/bookings/rooms': DoorOpen,
  };

  // Base navigation items (conditional based on role)
  // NOTE: Dashboard/Users/Resources hidden from main navigation as of user request
  // Routes still work if accessed directly. Can be restored by uncommenting below.
  // These items are still accessible via the Admin dashboard.
  const baseNavItems = [];

  // HIDDEN: Admin users see all pages (commented out - items hidden from navigation)
  // if (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
  //   baseNavItems.push(
  //     { path: '/dashboard', label: 'Dashboard' },
  //     { path: '/users', label: 'Users' },
  //     { path: '/resources', label: 'Resources' }
  //   );
  // }
  // Regular users don't see these pages in navigation

  // Plugin-based navigation items
  const pluginNavItems = [];
  if (isPluginEnabled('BOOKING')) {
    pluginNavItems.push(
      { path: '/bookings/my-bookings', label: t('navigation.myBookings') },
      { path: '/calendar', label: t('navigation.calendar') },
      { path: '/bookings/my-bookings?book=true', label: t('navigation.bookNow') },
      // { path: '/bookings/rooms', label: t('navigation.bookRoom') },
    );
  }

  // Combine all navigation items: base + plugins
  // Note: Settings and Admin removed from navbar (now in UserMenu)
  const navItems = [...baseNavItems, ...pluginNavItems];

  return (
    <div
      className="min-h-screen flex flex-col transition-colors duration-300"
      style={{
        backgroundColor: 'var(--theme-bg)',
        color: 'var(--theme-text)'
      }}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-50 shadow transition-all duration-300 flex-shrink-0"
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderBottom: `var(--theme-border-width) solid var(--theme-border)`,
          boxShadow: 'var(--theme-shadow)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          {/* Mobile Header - Icons spread evenly */}
          {isMobile ? (
            <div className="flex justify-evenly items-center h-16 py-2">
              {/* Logo - Compact on mobile */}
              <div className="flex-shrink-0">
                {settings?.navbarLogoData ? (
                  <img
                    src={settings.navbarLogoData}
                    alt={settings.appName || 'Logo'}
                    className="h-10 w-auto object-contain"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                  />
                ) : (
                  <span
                    className="text-lg font-bold"
                    style={{ color: 'var(--theme-accent)' }}
                  >
                    {settings?.appName || 'Chamama'}
                  </span>
                )}
              </div>

              {/* AI Support Bot */}
              <AImryAssistant />

              {/* Notification Bell */}
              <NotificationBell />

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User Menu */}
              <UserMenu />
            </div>
          ) : (
            /* Desktop Header - Original layout */
            <div className="flex justify-between items-center h-32 gap-4">
              {/* Logo and Navigation - flex-shrink to prevent overflow */}
              <div className="flex items-center min-w-0 flex-1">
                <div className="flex-shrink-0 flex items-center">
                  {settings?.navbarLogoData ? (
                    <img
                      src={settings.navbarLogoData}
                      alt={settings.appName || 'Logo'}
                      className={`w-auto object-contain transition-opacity duration-300 ${
                        settings.navbarLogoSize === 'sm' ? 'h-14' :
                        settings.navbarLogoSize === 'lg' ? 'h-42' :
                        'h-28'  // md default
                      }`}
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }}
                    />
                  ) : (
                    <span
                      className="text-2xl font-bold transition-colors duration-300"
                      style={{
                        color: 'var(--theme-accent)',
                        fontWeight: 'var(--theme-font-weight-heading)'
                      }}
                    >
                      {settings?.appName || 'Chamama Booking'}
                    </span>
                  )}
                </div>
                <nav className="typo-navigation flex space-x-8 overflow-x-auto" style={{ marginInlineStart: '2.5rem' }}>
                  {navItems.map((item) => {
                    const IconComponent = iconMap[item.path];
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="typo-navigation inline-flex items-center gap-2 px-1 pt-1 text-sm transition-colors duration-200"
                        style={{
                          color: isActive(item.path) ? 'var(--theme-accent)' : 'var(--theme-text-muted)',
                          borderBottom: isActive(item.path) ? `2px solid var(--theme-accent)` : '2px solid transparent',
                          fontWeight: isActive(item.path) ? 600 : 500
                        }}
                      >
                        {IconComponent && (
                          <IconComponent
                            size={18}
                            className="flex-shrink-0"
                          />
                        )}
                        <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* User Controls - flex-shrink-0 to prevent being pushed off-screen */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* AI Support Bot */}
                <AImryAssistant />

                {/* Notification Bell */}
                <NotificationBell />

                {/* Language Switcher */}
                <LanguageSwitcher />

                {/* User Menu */}
                <UserMenu />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 page-background-crt">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
